import type { ReactNode } from 'react';

/**
 * The guest shell's icons for the tile home and the help button: one family of
 * line icons on a 24 grid, one stroke weight, round ends, no fills
 * (docs/GUEST-REDESIGN.md, section 3). They are inline SVG in the bundle, so
 * they cost nothing to cache and work offline, which is why there is no icon
 * library: a dependency for fifteen paths would weigh more than the paths.
 *
 * Kept apart from parts/icons.tsx, which is the House Guide's own set and
 * belongs to that work. The shared shapes are drawn the same in both.
 *
 * They draw in currentColor, so a caller colors them with a token class
 * (text-accent, text-mark) and never with a hex value. Every one is decorative:
 * the word beside it carries the meaning, so each is hidden from screen readers.
 */

function Icon({
  children,
  className = 'h-6 w-6',
  strokeWidth = 1.6,
}: {
  children: ReactNode;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

type P = { className?: string };

/** Arrival Guide: a small sailboat. */
export function BoatIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M3 16h18l-2.4 4H5.4z" />
      <path d="M12 3v13" />
      <path d="M12 4.5c3.4 2 5.2 5.6 5.4 9.5H12" />
      <path d="M10.4 8C8.6 9.6 7.4 11.6 7 14h5" />
    </Icon>
  );
}

/** House Guide: a house with a door. */
export function HouseIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v10h13V10" />
      <path d="M10 20v-5.5h4V20" />
    </Icon>
  );
}

/** Island Guide: a palm tree. */
export function PalmIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M12.5 21c0-4.6.4-7.6 1.8-10.6" />
      <path d="M14.3 10.4c-.9-3-3.8-4.4-7.2-3.6" />
      <path d="M14.3 10.4c.6-3.2 3.4-4.8 7-3.6" />
      <path d="M14.3 10.4c-3-.3-5.4.8-6.6 3.6" />
      <path d="M14.3 10.4c2.4.8 4 2.8 4.2 5.6" />
      <path d="M3 21h18" />
    </Icon>
  );
}

/** Weather: a cloud with the sun behind it. */
export function CloudSunIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M12 2.5v1.8M4.6 5.4l1.3 1.3M19.4 5.4l-1.3 1.3M2.5 12h1.8M21.5 12h-1.8" />
      <path d="M15.9 12.6A4 4 0 1 0 10 8.5" />
      <path d="M13 21.5H7.2a4.6 4.6 0 1 1 4.4-6H13a3 3 0 0 1 0 6z" />
    </Icon>
  );
}

/** Running on sunshine: a plain sun, apart from the weather tile's sun and cloud. */
export function SunIcon({ className }: P) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </Icon>
  );
}

/** Translator: two speech bubbles. */
export function BubblesIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M4 3.5h9a2 2 0 0 1 2 2V10a2 2 0 0 1-2 2H8.5L5 15v-3H4a2 2 0 0 1-2-2V5.5a2 2 0 0 1 2-2z" />
      <path d="M17 9.5h3a2 2 0 0 1 2 2V16a2 2 0 0 1-2 2h-1v3l-3.5-3H12a2 2 0 0 1-1.6-.8" />
      <path d="M6 7.5h5" />
    </Icon>
  );
}

/** Who sleeps where: a bed. */
export function BedIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M3 20V6" />
      <path d="M3 16h18" />
      <path d="M21 20v-6a3 3 0 0 0-3-3h-8v5" />
      <path d="M6.5 12.6h.01" />
    </Icon>
  );
}

/** The help button: an exclamation mark, drawn heavier so it reads at a glance. */
export function AlertIcon({ className }: P) {
  return (
    <Icon className={className} strokeWidth={2.6}>
      <path d="M12 5.5v8" />
      <path d="M12 18.4h.01" />
    </Icon>
  );
}

export function PhoneIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </Icon>
  );
}

export function AidIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z" />
    </Icon>
  );
}

export function BellIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M3 18.5h18" />
      <path d="M5 18.5a7 7 0 0 1 14 0" />
      <path d="M12 8.5V6" />
      <path d="M10 6h4" />
    </Icon>
  );
}

export function FlagIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 4 2 4H5" />
    </Icon>
  );
}

export function MessageIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M4 5h16v11H9.5L4 20z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </Icon>
  );
}

export function ChevronIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="m9 5 7 7-7 7" />
    </Icon>
  );
}

export function CloseIcon({ className }: P) {
  return (
    <Icon className={className}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
  );
}

export function LockIcon({ className }: P) {
  return (
    <Icon className={className}>
      <rect x="5" y="11" width="14" height="9.5" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Icon>
  );
}
